import type { ColDef, ICellRendererParams, SetFilterValuesFuncParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IASOKeyword } from '~/types/aso'

import ActionMenu from '~/components/ActionMenu'
import ASOTitleCellRenderer from '~/components/CellRenderers/ASOTitleCellRenderer'
import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import LargeFlagCellRenderer from '~/components/CellRenderers/LargeFlagCellRenderer'
import ScoreCellRenderer from '~/components/CellRenderers/ScoreCellRenderer'
import { actionsColDef, badgeColDef, scoreColDef } from '~/utils/table'

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IASOKeyword>): ReactElement => {
  if (data) {
    const { handleRedirect } = context
    const { country } = data

    const menu: Menu = {
      navigations: [
        {
          action: () => handleRedirect(country),
          label: 'Display advanced view',
          type: 'internal',
        },
      ],
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef[] = [
  {
    cellRenderer: LargeFlagCellRenderer,
    field: 'country',
    filter: 'agTextColumnFilter',
    headerName: 'Country',
    headerTooltip: 'Country',
    minWidth: 200,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
  },
  scoreColDef({
    cellRenderer: ScoreCellRenderer,
    field: 'health_score',
    filter: false,
    headerName: 'Health score',
    headerTooltip: 'Health Score',
    valueGetter: ({ data }: ValueGetterParams<IASOKeyword>) => ({
      type: 'health',
      value: data?.health_score || 0,
    }),
  }),
  scoreColDef({
    cellRenderer: ScoreCellRenderer,
    field: 'opportunity_score',
    filter: false,
    headerName: 'Opportunity score',
    headerTooltip: 'Opportunity score',
    valueGetter: ({ data }: ValueGetterParams<IASOKeyword>) => ({
      type: 'opportunity',
      value: data?.opportunity_score || 0,
    }),
  }),
  {
    cellRenderer: ASOTitleCellRenderer,
    field: 'title',
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }: ValueGetterParams<IASOKeyword>) => `${data?.title} ${data?.subtitle}`,
    headerName: 'Current title',
    headerTooltip: 'Current title',
    menuTabs: ['filterMenuTab'],
    minWidth: 200,
    suppressMovable: true,
    width: 250,
  },
  badgeColDef(BadgeCellRenderer, {
    autoHeight: true,
    field: 'keywords',
    filterParams: {
      values: ({ context, success }: SetFilterValuesFuncParams) => success(context.distinctKeywords),
    },
    filterValueGetter: ({ data }: ValueGetterParams<IASOKeyword>) => data?.keywords,
    flex: 1,
    headerName: 'Current keywords',
    headerTooltip: 'Current keywords',
    valueGetter: ({ data }: ValueGetterParams<IASOKeyword>) => {
      if (data) {
        const { keywords } = data

        return {
          data:
            keywords?.map((keyword) => ({
              id: keyword,
              name: keyword,
            })) || [],
          maxItems: 5,
          type: 'keyword',
        }
      }

      return null
    },
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
